import { PythonTopicExercise } from '../../../../types/python-topic';

export const disenoEspecificacionExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Documentar funciones con docstrings',
    description: 'Aprende a documentar funciones usando docstrings en Python.',
    initialCode: `# Documenta esta función con un docstring
def calcular_area_rectangulo(base, altura):
    """
    # Completa el docstring:
    # - Descripción de qué hace la función
    # - Parámetros (base, altura)
    # - Valor de retorno
    """
    return base * altura

# Prueba la función
area = calcular_area_rectangulo(5, 3)
print(f"Área: {area}")`,
    solution: `# Documenta esta función con un docstring
def calcular_area_rectangulo(base, altura):
    """
    Calcula el área de un rectángulo.
    
    Parámetros:
    base (float): La base del rectángulo
    altura (float): La altura del rectángulo
    
    Retorna:
    float: El área del rectángulo (base * altura)
    """
    return base * altura

# Prueba la función
area = calcular_area_rectangulo(5, 3)
print(f"Área: {area}")`,
    testCases: [],
    hints: [
      'Los docstrings van entre triple comillas """',
      'Incluye descripción, parámetros y retorno',
      'Sigue el formato estándar de Python',
      'Los docstrings son accesibles con help()'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué documentar funciones?',
      content: `**Documentar funciones** con docstrings hace tu código más mantenible y fácil de entender. Otros desarrolladores (y tú en el futuro) entenderán qué hace cada función.

### ¿Por qué este ejercicio?
- **Mantenibilidad**: Código más fácil de mantener
- **Colaboración**: Otros desarrolladores entienden tu código
- **Profesionalismo**: Buenas prácticas de desarrollo
- **Auto-documentación**: help() muestra la documentación

### En el mundo real:
En proyectos profesionales, todas las funciones públicas tienen docstrings. Sin documentación, un desarrollador nuevo tardaría mucho más en entender el código. Con docstrings claros, puede entender rápidamente qué hace cada función.`,
      keyPoints: [
        'Los docstrings van entre triple comillas """',
        'Incluyen descripción, parámetros y retorno',
        'Siguen convenciones estándar de Python',
        'Son accesibles con help() y herramientas de documentación'
      ],
      realWorldExample: 'En una librería de código abierto, cada función tiene docstrings que explican su propósito, parámetros y retorno, permitiendo a otros desarrolladores usarla fácilmente.'
    },
    theory: `# Documentar Funciones con Docstrings

## ¿Qué es un Docstring?

Un **docstring** es una cadena de texto que documenta una función, clase o módulo. Va entre triple comillas al inicio.

## Formato Estándar

\`\`\`python
def calcular_area(base, altura):
    """
    Calcula el área de un rectángulo.
    
    Parámetros:
    base (float): La base del rectángulo
    altura (float): La altura del rectángulo
    
    Retorna:
    float: El área del rectángulo
    """
    return base * altura
\`\`\`

## Acceder a Docstrings

Puedes acceder a docstrings con \`help()\`:

\`\`\`python
help(calcular_area)
\`\`\`

## Buenas Prácticas

- **Claridad**: Explica qué hace la función
- **Parámetros**: Describe cada parámetro
- **Retorno**: Explica qué retorna
- **Ejemplos**: Incluye ejemplos si es útil

## Ventajas

- **Mantenibilidad**: Código más fácil de mantener
- **Colaboración**: Otros entienden tu código
- **Profesionalismo**: Buenas prácticas
- **Auto-documentación**: Herramientas generan documentación automáticamente`,
    tips: [
      'Usa triple comillas """ para docstrings',
      'Incluye descripción, parámetros y retorno',
      'Sigue el formato estándar de Python',
      'Los docstrings son accesibles con help()',
      'Sé claro y conciso en la documentación'
    ],
    advice: [
      'Documenta todas las funciones públicas',
      'Sigue convenciones estándar de Python',
      'Actualiza docstrings cuando cambies funciones',
      'La documentación es tan importante como el código'
    ]
  }
];
